/**
 * Presence for one user — the green/grey dot on a chat avatar.
 *
 * Reads `GET /api/v5/users/:userId/online`, which answers from the relay's
 * presence: a webapp user is online while one of their relay streams is open
 * (phones keep the legacy websocket's own presence). Each read also registers this
 * user as a watcher, so the relay pushes `userPresence.changed` on their own
 * `user:` channel the moment the watched user's first stream opens or last one
 * closes, and `UserRealtimeProvider` refreshes this query — office and member app
 * alike. Lives in `src/api/` rather than a feature `_api/` folder because several
 * slices need it (`features/office`, `features/my`, `features/shared/messaging`).
 */

import type { CustomQueryOptions } from "@birdwatch/client";
import { useGetUsersOnlineByUserId } from "@birdwatch/client";

/**
 * How often a mounted presence dot re-reads. Each read renews the watch the push
 * depends on (it lapses after 150 s), and it is the backstop for what is never
 * announced: a stream that dies without a close, and the phones' presence.
 */
const USER_ONLINE_POLL_MS = 60_000;

/**
 * Read whether `userId` is currently online.
 *
 * Callers that force the dot green client-side (yourself, the Birdwatch team
 * admin) should pass `enabled: false` rather than reading the result and
 * ignoring it, so no request is made at all.
 */
export const useUserOnline = (userId: string, options?: CustomQueryOptions) =>
  useGetUsersOnlineByUserId({ userId }).get({
    refetchInterval: USER_ONLINE_POLL_MS,
    ...options,
  });
